import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import {
  type SmartGuideLine,
  computeSmartGuides,
  extractLayoutSnapLines,
} from '@/lib/guides/smart-guides'
import { buildMultiSelectShapeSnap } from '../helpers/multi-select-geometry'
import type { InteractionState } from '../types/interaction-state'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { MultiSelectShapeGeomSnap } from '@/lib/transforms/types/multi-select-shape-geom-snap'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerProps } from '../types/shape-manager-props'

type MoveInteraction = Pick<InteractionState, 'shapeId' | 'initial' | 'multiMoveInitials'>

/** A canvas-unit move delta. */
export interface MoveDelta {
  dx: number
  dy: number
}

/** The shapes a move drags, each with its gesture-start position — the smart-guide subject. */
export function movingShapesForSmartGuides(
  current: MoveInteraction,
  shapes: ShapeData[]
): Array<{ shape: ShapeData; initialX: number; initialY: number }> {
  return current.multiMoveInitials
    ? Object.entries(current.multiMoveInitials).flatMap(([id, initialPos]) => {
        const shape = shapes.find((candidate) => candidate.id === id)

        return shape
          ? [
              {
                shape,
                initialX: initialPos.x,
                initialY: initialPos.y,
              },
            ]
          : []
      })
    : [
        {
          shape: current.initial,
          initialX: current.initial.x,
          initialY: current.initial.y,
        },
      ]
}

export interface MoveSmartGuideDeps {
  shapes: ShapeData[]
  getOtherShapeBounds: (excludeIds: Set<string>) => ObjectBounds[]
  getMovedSelectionBounds: (
    movingShapes: Array<{ shape: ShapeData; initialX: number; initialY: number }>,
    rawDx: number,
    rawDy: number
  ) => ObjectBounds | null
  effectiveSnapGuideDistance: number
  /** Ruler-guide snap lines, already gated on the snap-to-guides setting. */
  layoutSnapLines: ReturnType<typeof extractLayoutSnapLines> | undefined
}

/**
 * Smart-guide snap for a move, on top of the guide/grid-snapped delta: the guides to show and the
 * adjusted delta, or `null` when the moving selection has no bounds to snap.
 */
export function computeMoveSmartGuideSnap(
  current: MoveInteraction,
  snappedDelta: MoveDelta,
  {
    shapes,
    getOtherShapeBounds,
    getMovedSelectionBounds,
    effectiveSnapGuideDistance,
    layoutSnapLines,
  }: MoveSmartGuideDeps
): { guides: SmartGuideLine[]; delta: MoveDelta } | null {
  const movingIds = new Set(
    current.multiMoveInitials ? Object.keys(current.multiMoveInitials) : [current.shapeId]
  )
  const otherBounds = getOtherShapeBounds(movingIds)
  const movingShapes = movingShapesForSmartGuides(current, shapes)
  const movingBounds = getMovedSelectionBounds(movingShapes, snappedDelta.dx, snappedDelta.dy)

  if (!movingBounds) {
    return null
  }

  const result = computeSmartGuides(
    movingBounds,
    otherBounds,
    effectiveSnapGuideDistance,
    layoutSnapLines
  )

  return {
    guides: result.guides,
    delta: {
      dx: snappedDelta.dx + result.snapAdjust.dx,
      dy: snappedDelta.dy + result.snapAdjust.dy,
    },
  }
}

export interface MoveUpdateDeps {
  updateShape: ShapeManagerRef['updateShape']
  setMultiSelectChrome: Dispatch<SetStateAction<MultiSelectChrome | null>>
  multiSelectSyncPrevShapesRef: MutableRefObject<Record<string, MultiSelectShapeGeomSnap> | null>
  /** Reads the live shape list; called after updates so it sees them. */
  getShapes: () => ShapeData[]
}

/** Live move frame: place every dragged shape at its start position plus the snapped delta. */
export function applyLiveMoveUpdates(
  current: InteractionState,
  snappedDelta: MoveDelta,
  { updateShape, setMultiSelectChrome, multiSelectSyncPrevShapesRef, getShapes }: MoveUpdateDeps
): void {
  if (current.multiMoveInitials) {
    document.body.style.cursor = 'move'

    Object.entries(current.multiMoveInitials).forEach(([id, pos]) => {
      updateShape(id, {
        x: pos.x + snappedDelta.dx,
        y: pos.y + snappedDelta.dy,
      })
    })

    if (current.multiChromePivotStart) {
      setMultiSelectChrome((prev) =>
        prev
          ? {
              ...prev,
              pivotX: current.multiChromePivotStart!.x + snappedDelta.dx,
              pivotY: current.multiChromePivotStart!.y + snappedDelta.dy,
            }
          : prev
      )
    }
    // Keep the rigid-translation baseline in step with live multi-select move updates.
    // Without this, the post-interaction sync effect sees the whole move again and
    // applies an extra chrome pivot translation (box appears ~2x moved distance).

    multiSelectSyncPrevShapesRef.current = buildMultiSelectShapeSnap(
      Object.keys(current.multiMoveInitials),
      getShapes()
    )
  } else {
    updateShape(current.shapeId, {
      x: current.initial.x + snappedDelta.dx,
      y: current.initial.y + snappedDelta.dy,
    })
  }
}

/** Pointer-up of a multi-select move: final positions, chrome pivot, then one batch commit. */
export function commitMultiMove(
  interaction: InteractionState & Required<Pick<InteractionState, 'multiMoveInitials'>>,
  snappedMoveDelta: MoveDelta,
  {
    updateShape,
    setMultiSelectChrome,
    multiSelectSyncPrevShapesRef,
    getShapes,
    onBatchShapesCommitted,
  }: MoveUpdateDeps & { onBatchShapesCommitted: ShapeManagerProps['onBatchShapesCommitted'] }
): void {
  for (const [id, initial] of Object.entries(interaction.multiMoveInitials)) {
    updateShape(id, {
      x: initial.x + snappedMoveDelta.dx,
      y: initial.y + snappedMoveDelta.dy,
    })
  }

  if (interaction.multiChromePivotStart) {
    setMultiSelectChrome((prev) =>
      prev
        ? {
            ...prev,
            pivotX: interaction.multiChromePivotStart!.x + snappedMoveDelta.dx,
            pivotY: interaction.multiChromePivotStart!.y + snappedMoveDelta.dy,
          }
        : prev
    )
  }

  multiSelectSyncPrevShapesRef.current = buildMultiSelectShapeSnap(
    Object.keys(interaction.multiMoveInitials),
    getShapes()
  )

  // Commit each moved shape (one undo step + persistence batch)
  const allShapes = getShapes()
  const EPS_POS = 1e-4
  const batchItems: BatchShapeTransformItem[] = []

  for (const id of Object.keys(interaction.multiMoveInitials)) {
    const initial = interaction.multiMoveInitials[id]
    const nx = initial.x + snappedMoveDelta.dx
    const ny = initial.y + snappedMoveDelta.dy

    if (Math.abs(initial.x - nx) <= EPS_POS && Math.abs(initial.y - ny) <= EPS_POS) {
      continue
    }

    const shape = allShapes.find((s) => s.id === id)

    if (shape) {
      batchItems.push({
        shapeId: id,
        before: { x: initial.x, y: initial.y },
        after: { x: nx, y: ny },
        shape,
      })
    }
  }

  if (batchItems.length > 0) {
    onBatchShapesCommitted?.(batchItems, 'move')
  }
}
